/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  reaction,
  readonly,
  withAssign,
  withInit,
} from '@reatom/framework'

import {
  Network,
  selectedNetworkAtom,
  setNetworkAction,
} from '@root/entities/network'
import { PairAddress } from '@root/entities/pair/model/PairAddress'
import { TradeRoute } from '@root/features/routing'
import { STORAGE_PREFIX } from '@root/shared'
import {
  browserHistory,
  routerActions,
  routerLocationAtom,
} from '@root/shared/lib/react-router'
import { clearDataRequestedEvent } from '@root/shared/lib/storage'

import type { Location } from 'react-router'

const LATEST_PAIR_KEY = `${STORAGE_PREFIX}/v1/latestPair`

const reatomTradingPairAddress = (network: Network.Network) => {
  const addressAtom = atom<PairAddress>(Network.mainUsdPair(network)).pipe(
    withInit((ctx) => {
      const params = extractTradingPairFromRouter(ctx.get(routerLocationAtom))
      if (params && params.network === network) {
        return params.pair
      }

      const key = `${LATEST_PAIR_KEY}/${network}`
      const persisted = localStorage.getItem(key)
      return persisted ? PairAddress(persisted) : Network.mainUsdPair(network)
    }),
  )

  const setAction = action((ctx, address: PairAddress) => {
    addressAtom(ctx, address)
    localStorage.setItem(`${LATEST_PAIR_KEY}/${network}`, address)
  }, 'setAction')

  return addressAtom.pipe(
    readonly,
    withAssign(() => ({
      setAction,
    })),
  )
}

const addressByNetwork = Network.byNetworkRecord((network) =>
  reatomTradingPairAddress(network),
)
const tradingPairAddressModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)

  // Typed as total and not total at runtime: a selected network that is not
  // one of ours can still be sitting in local storage from before this was
  // guarded, and spying on undefined throws where nothing catches it —
  // which stopped the whole app, not just this panel.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- total in the types, not at runtime: see above
  return addressByNetwork[network] ?? addressByNetwork[Network.Solana]
}, 'tradingPairAddressAtom')

export const tradingPairAddressAtom = atom((ctx) => {
  const model = ctx.spy(tradingPairAddressModelAtom)
  return ctx.spy(model)
}, 'tradingPairAddressAtom')

clearDataRequestedEvent.onCall(() => {
  for (const it of Network.networks) {
    localStorage.removeItem(`${LATEST_PAIR_KEY}/${it}`)
  }
})

export const changeTradingPairAction = action(
  (ctx, pairAddress: PairAddress) => {
    const model = ctx.get(tradingPairAddressModelAtom)

    model.setAction(ctx, pairAddress)
  },
  'changeTradingPairAction',
)

/**
 * The pair and network a trade url names, when it names ones we have.
 *
 * Taking the segment as a network without checking is what made a single
 * `/trade/sol/…` visit brick the app: the unknown value was stored as the
 * selected network, every later start looked for a model under it, found
 * undefined, and threw before the first screen rendered — on every route,
 * out of local storage, until it was cleared by hand.
 */
export const extractTradingPairFromRouter = (location: Location) => {
  const match = TradeRoute.match(location.pathname)

  const pair = match?.params.pairAddress
  const network = match?.params.network
    ? Network.fromPath(match.params.network)
    : undefined

  if (!pair || !network) {
    return undefined
  }

  return { pair: PairAddress(pair), network }
}

// Integration with url router
export const syncTradingPairWithRouterAction = action(
  (ctx, location: Location) => {
    const params = extractTradingPairFromRouter(location)
    if (!params) {
      return
    }

    changeTradingPairAction(ctx, params.pair)

    // Only when it actually changed. Every location write lands here, and
    // `setNetworkAction` tells the server unconditionally, so a solana→solana
    // navigation used to POST `change-network` — twice, because opening a pair
    // writes the location twice. Two writes nobody reads, in the same wave as
    // the reads the page is waiting for.
    //
    // The guard is here rather than in `setNetworkAction`: switching network
    // explicitly must always sync, even to the value the client already holds.
    if (params.network !== ctx.get(selectedNetworkAtom)) {
      setNetworkAction(ctx, params.network)
    }
  },
)

export const syncTradingPairWithRouterReaction = reaction((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const pair = ctx.spy(tradingPairAddressAtom)

  const params = extractTradingPairFromRouter(browserHistory.location)

  if (!params || (params.network === network && params.pair === pair)) {
    return
  }

  routerActions.navigate(ctx, TradeRoute.build({ pairAddress: pair, network }))
})
